/**
 * DARIA Video Carousel
 * No third-party dependencies.
 * Supports multiple instances of the section on the same page.
 */
(() => {
  const SELECTOR = '[data-daria-video-carousel]';
  const initialized = new WeakSet();

  class DariaVideoCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-dvc-track]');
      this.cards = Array.from(root.querySelectorAll('[data-dvc-card]'));
      this.prevButton = root.querySelector('[data-dvc-prev]');
      this.nextButton = root.querySelector('[data-dvc-next]');
      this.autoplay = root.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.activeVideo = null;
      this.drag = null;
      this.intersectionObserver = null;

      if (!this.track || !this.cards.length) return;

      this.bindCardControls();
      this.bindNavigation();
      this.bindKeyboard();
      this.bindDesktopDrag();
      this.observeVideos();
      this.updateArrows();

      requestAnimationFrame(() => this.updateArrows());
    }

    getVideo(card) {
      return card ? card.querySelector('video') : null;
    }

    getCard(video) {
      return video ? video.closest('[data-dvc-card]') : null;
    }

    getPlayButton(card) {
      return card ? card.querySelector('[data-dvc-play]') : null;
    }

    getSoundButton(card) {
      return card ? card.querySelector('[data-dvc-sound]') : null;
    }

    bindCardControls() {
      this.cards.forEach((card) => {
        const video = this.getVideo(card);
        const playButton = this.getPlayButton(card);
        const soundButton = this.getSoundButton(card);

        if (!video || !playButton) return;

        video.muted = true;
        video.playsInline = true;

        playButton.addEventListener('click', () => {
          if (video.paused) {
            this.play(video, { userInitiated: true });
          } else {
            this.pause(video);
          }
        });

        video.addEventListener('play', () => this.setPlayingState(video, true));
        video.addEventListener('pause', () => this.setPlayingState(video, false));
        video.addEventListener('ended', () => this.setPlayingState(video, false));

        if (soundButton) {
          soundButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            video.muted = !video.muted;
            soundButton.setAttribute('aria-pressed', String(!video.muted));
            soundButton.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');

            if (video.paused) {
              this.play(video, { userInitiated: true });
            }
          });
        }
      });
    }

    async play(video, { userInitiated = false } = {}) {
      if (!video) return;

      if (this.activeVideo && this.activeVideo !== video) {
        this.pause(this.activeVideo);
      }

      this.activeVideo = video;

      // Autoplay must remain muted. A direct user action preserves the user's
      // current mute choice instead of forcing audio on unexpectedly.
      if (!userInitiated) video.muted = true;

      try {
        await video.play();
      } catch (error) {
        // Browsers may block autoplay. The visible play button remains available.
        this.setPlayingState(video, false);
      }
    }

    pause(video) {
      if (!video) return;
      video.pause();
      if (this.activeVideo === video) this.activeVideo = null;
    }

    pauseAll(except = null) {
      this.cards.forEach((card) => {
        const video = this.getVideo(card);
        if (video && video !== except && !video.paused) this.pause(video);
      });
    }

    setPlayingState(video, playing) {
      const card = this.getCard(video);
      const playButton = this.getPlayButton(card);
      if (!card || !playButton) return;

      card.classList.toggle('is-playing', playing);
      playButton.setAttribute('aria-pressed', String(playing));
      playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');

      if (playing) {
        this.pauseAll(video);
        this.activeVideo = video;
      } else if (this.activeVideo === video) {
        this.activeVideo = null;
      }
    }

    observeVideos() {
      if (!('IntersectionObserver' in window)) return;

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            const video = this.getVideo(card);
            if (!video) return;

            if (entry.intersectionRatio < 0.35 && !video.paused) {
              this.pause(video);
              return;
            }

            if (
              this.autoplay &&
              !this.reducedMotion &&
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.72 &&
              !this.activeVideo
            ) {
              this.play(video);
            }
          });
        },
        {
          root: this.track,
          threshold: [0, 0.35, 0.72, 1]
        }
      );

      this.cards.forEach((card) => this.intersectionObserver.observe(card));
    }

    bindNavigation() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollByCard(-1));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollByCard(1));
      }

      this.track.addEventListener('scroll', () => {
        window.requestAnimationFrame(() => this.updateArrows());
      }, { passive: true });

      window.addEventListener('resize', () => this.updateArrows(), { passive: true });
    }

    getCurrentCardIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const isRtl = window.getComputedStyle(this.track).direction === 'rtl';
      const startEdge = isRtl ? trackRect.right : trackRect.left;

      let closestIndex = 0;
      let closestDistance = Infinity;

      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const cardStart = isRtl ? rect.right : rect.left;
        const distance = Math.abs(cardStart - startEdge);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    scrollByCard(direction) {
      const currentIndex = this.getCurrentCardIndex();
      const targetIndex = Math.max(0, Math.min(this.cards.length - 1, currentIndex + direction));
      const targetCard = this.cards[targetIndex];
      if (!targetCard) return;

      targetCard.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }

    isFullyVisible(card) {
      if (!card) return true;
      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const tolerance = 3;

      return (
        cardRect.left >= trackRect.left - tolerance &&
        cardRect.right <= trackRect.right + tolerance
      );
    }

    updateArrows() {
      if (!this.prevButton && !this.nextButton) return;

      if (this.prevButton) this.prevButton.disabled = this.isFullyVisible(this.cards[0]);
      if (this.nextButton) this.nextButton.disabled = this.isFullyVisible(this.cards[this.cards.length - 1]);
    }

    bindKeyboard() {
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.scrollByCard(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.scrollByCard(1);
        }
      });
    }

    bindDesktopDrag() {
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        if (event.target.closest('a, button')) return;

        this.drag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScrollLeft: this.track.scrollLeft,
          moved: false
        };

        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
      });

      this.track.addEventListener('pointermove', (event) => {
        if (!this.drag || event.pointerId !== this.drag.pointerId) return;

        const delta = event.clientX - this.drag.startX;
        if (Math.abs(delta) > 4) this.drag.moved = true;
        this.track.scrollLeft = this.drag.startScrollLeft - delta;
      });

      const finishDrag = (event) => {
        if (!this.drag || event.pointerId !== this.drag.pointerId) return;

        this.track.classList.remove('is-dragging');
        try { this.track.releasePointerCapture(event.pointerId); } catch (_) {}
        this.drag = null;
      };

      this.track.addEventListener('pointerup', finishDrag);
      this.track.addEventListener('pointercancel', finishDrag);
      this.track.addEventListener('pointerleave', (event) => {
        if (this.drag && event.buttons === 0) finishDrag(event);
      });
    }

    destroy() {
      this.pauseAll();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }
  }

  function init(scope = document) {
    const roots = scope.matches?.(SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(SELECTOR) || []);

    roots.forEach((root) => {
      if (initialized.has(root)) return;
      initialized.add(root);
      root.__dariaVideoCarousel = new DariaVideoCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Shopify theme editor support: sections can be inserted/re-rendered without a full page load.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SELECTOR)
      ? event.target
      : event.target.querySelector?.(SELECTOR);
    root?.__dariaVideoCarousel?.destroy?.();
  });
})();
